import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { CashflowSignalLevel } from "@/types/apex";

const QUERY_KEY = ["apex-cashflow-signal"];

export function useCashflowSignal() {
  const queryClient = useQueryClient();

  const { data: activeSignal, isLoading } = useQuery({
    queryKey: [...QUERY_KEY, "active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cashflow_signal_log" as any)
        .select("*")
        .lte("effective_from", new Date().toISOString().split("T")[0])
        .or("effective_to.is.null,effective_to.gte." + new Date().toISOString().split("T")[0])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as any) as { signal: CashflowSignalLevel; set_by: string; reason: string; effective_from: string; effective_to: string | null } | null;
    },
    staleTime: 60_000, // 60s — brief spec says don't over-poll this
  });

  const { data: history } = useQuery({
    queryKey: [...QUERY_KEY, "history"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cashflow_signal_log" as any)
        .select("*")
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data;
    },
  });

  const setSignalMutation = useMutation({
    mutationFn: async (entry: {
      signal: CashflowSignalLevel;
      set_by: string;
      reason: string;
      effective_from: string;
      effective_to?: string;
    }) => {
      const { error } = await supabase
        .from("cashflow_signal_log" as any)
        .insert(entry);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success("Cashflow signal updated.");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return {
    signal: (activeSignal?.signal ?? "GREEN") as CashflowSignalLevel,
    activeSignal,
    history: history ?? [],
    isLoading,
    setSignal: setSignalMutation.mutate,
    isSetting: setSignalMutation.isPending,
  };
}
